import React, { useState, useEffect, useCallback } from "react";
import DesktopView from "../layout/DesktopView";
import FineTunedModelCard from "./FineTunedModelCard";
import FineTunedModelEditor from "./FineTunedModelEditor";
import { modelService } from "../../services/modelService";
import { useToast } from "../common/ToastContext";

const FineTunedModelDesktop = () => {
  const [fineTunedModels, setFineTunedModels] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const { showToast } = useToast();

  const fetchFineTunedModels = useCallback(async () => {
    try {
      const response = await modelService.getFineTuneList();
      const sortedData = response.sort((a, b) => a.name.localeCompare(b.name));
      setFineTunedModels(sortedData);
    } catch (error) {
      console.error("Error fetching fine-tuned models:", error);
      showToast("Failed to fetch fine-tuned models", "error");
    }
  }, [showToast]);

  useEffect(() => {
    fetchFineTunedModels();
  }, [fetchFineTunedModels]);

  const handleSearch = (term) => {
    // Search is handled internally by DesktopView
  };

  const handleFineTunedModelClick = async (data) => {
    try {
      const fullData = await modelService.getFineTuneById(data.id);
      setSelectedItem(fullData);
      setIsEditorOpen(true);
    } catch (error) {
      console.error("Error fetching full fine-tuned model data:", error);
      showToast("Failed to fetch fine-tuned model details", "error");
    }
  };

  const handleSaveFineTunedModel = (savedModel) => {
    setFineTunedModels((prevModels) => {
      const updatedModels = prevModels.map((model) =>
        model.id === savedModel.id ? savedModel : model
      );
      if (!prevModels.find((model) => model.id === savedModel.id)) {
        updatedModels.push(savedModel);
      }
      return updatedModels.sort((a, b) => a.name.localeCompare(b.name));
    });
    setIsEditorOpen(false);
    setSelectedItem(null);
    fetchFineTunedModels(); // Refresh the list to ensure we have the latest data
  };

  const handleCancelFineTuneJob = async (jobId) => {
    try {
      await modelService.cancelFineTuneJob(jobId);
      await fetchFineTunedModels(); // Refresh the list
      setIsEditorOpen(false);
      setSelectedItem(null);
      showToast("Fine-tune job cancelled successfully", "success");
    } catch (error) {
      console.error("Error cancelling fine-tune job:", error);
      showToast("Failed to cancel fine-tune job", "error");
    }
  };

  const handleAddNewFineTunedModel = () => {
    setSelectedItem(null);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      <DesktopView
        items={fineTunedModels}
        selectedItem={selectedItem}
        onSearch={handleSearch}
        searchPlaceholder="Filter fine-tuned models ..."
        contextMenuItems={[]}
        onItemClick={handleFineTunedModelClick}
        onSave={handleSaveFineTunedModel}
        onDelete={handleCancelFineTuneJob}
        Card={FineTunedModelCard}
        addNewItem={handleAddNewFineTunedModel}
        eventName="fineTunedModelCreated"
      />
      {isEditorOpen && (
        <FineTunedModelEditor
          item={selectedItem}
          onSave={handleSaveFineTunedModel}
          onClose={handleCloseEditor}
          onCancel={handleCancelFineTuneJob}
        />
      )}
    </>
  );
};

export default FineTunedModelDesktop;
